import { FC, useMemo } from 'react'

import DropdownBoldArrowIcon from 'src/components/ui/atoms/Icons/DropdownBoldArrowIcon'
import SidebarMenuItem from 'src/components/ui/atoms/SidebarMenuItem'
import { SidebarMenuItemContext } from 'src/components/ui/atoms/SidebarMenuItem/SidebarMenuItemProvider'
import useLoadDashboard from 'src/hooks/useLoadDashboard'
import useProductMatchParams from 'src/hooks/useProductMatchParams'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { AppMenuCountryInterface } from 'src/types/AppMenuInterface'
import { SIDEBAR_MENU_HOVER_ENTER_DELAY } from 'src/utils/constants/SidebarMenu'

interface SidebarProductCountriesProps {
  dashboards: AppMenuCountryInterface[]
  subProductCode: string
  link: string
  isVisible?: boolean
  label?: string
}

const SidebarProductCountries: FC<SidebarProductCountriesProps> = ({
  dashboards: countries,
  subProductCode,
  link,
  isVisible,
  label,
}) => {
  const routeParams = useProductMatchParams()
  const programId = routeParams?.params?.program
  const subProduct = routeParams?.params?.subProduct
  const { setSelectedDashboard, selectedDashboard } = useSelectedDashboardStore()
  const { handleGetDashboard } = useLoadDashboard(subProductCode)

  const hasProgramIdActive = useMemo(() => {
    if (!programId || subProduct !== subProductCode || selectedDashboard) {
      return false
    }
    return countries.some(country => country.id.toString() === programId?.toString())
  }, [programId, countries, subProduct, subProductCode, selectedDashboard])

  const handleClickCountry = () => {
    setSelectedDashboard(null)
    handleGetDashboard({ id: null, programId: programId?.toString() })
  }

  if (countries.length === 1) {
    const country = countries[0]

    return (
      <SidebarMenuItem
        label={country.label}
        active={hasProgramIdActive && country.id.toString() === programId?.toString()}
        className="min-w-auto bg-white pl-12"
        isVisible={isVisible}
        to={`${link}/${country.id}`}
        onClick={handleClickCountry}
      />
    )
  }

  return (
    <SidebarMenuItem
      label={label || 'Market Analysis'}
      active={hasProgramIdActive}
      onClick={() => {}}
      className="pl-12"
      rightIcon={<DropdownBoldArrowIcon direction="right" className="text-hit-gray" />}
      isVisible={isVisible}
      menu={{
        isOpen: false,
        openOnHover: true,
        delay: SIDEBAR_MENU_HOVER_ENTER_DELAY,
        content: (
          <SidebarMenuItemContext.Consumer>
            {contextProps => (
              <>
                {countries.map(country => (
                  <SidebarMenuItem
                    key={country.id}
                    label={country.label}
                    active={hasProgramIdActive && country.id?.toString() === programId?.toString()}
                    className="min-w-auto bg-white pl-4"
                    to={`${link}/${country.id}`}
                    onClick={() => {
                      handleClickCountry()
                      contextProps?.closeHover()
                    }}
                    isOnModal
                  />
                ))}
              </>
            )}
          </SidebarMenuItemContext.Consumer>
        ),
      }}
    />
  )
}

export default SidebarProductCountries
